import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  AuthShell,
  Field,
  focusFirstError,
  PasswordField,
  PRIMARY_BUTTON_CLASS,
} from "../components/auth-form.tsx";
import { ErrorMessage } from "../components/legacy/ui.tsx";
import { signIn } from "../lib/auth-client.ts";
import {
  collect,
  type Errors,
  parseAuthSearch,
  validateEmail,
  validateRequired,
} from "../lib/auth-validation.ts";

export const Route = createFileRoute("/sign-in")({
  component: SignIn,
  validateSearch: parseAuthSearch,
});

type FormField = "email" | "password";

function SignIn() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [errors, setErrors] = useState<Errors<FormField>>({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const check = () =>
    collect<FormField>({
      email: validateEmail(email),
      // Not validatePassword: an existing password predates our rules, and
      // telling someone their real password is too short is nonsense.
      password: validateRequired(password, "password"),
    });

  /** Re-validate as they type only once they have seen an error. */
  const revalidate = () => {
    if (submitted) setErrors(check());
  };

  const login = async () => {
    setSubmitted(true);
    setFormError("");
    const found = check();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found);
      return;
    }

    setBusy(true);
    const result = await signIn.email({ email: email.trim(), password, rememberMe });
    setBusy(false);
    if (result.error) {
      // Which half was wrong is deliberately not said: it would confirm
      // whether an address has an account here.
      setFormError(result.error.message ?? "Invalid email or password");
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["config"] });
    if (redirect) navigate({ href: redirect });
    else navigate({ to: "/account" });
  };

  return (
    <AuthShell>
      <form
        noValidate
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          login();
        }}
      >
        {formError !== "" && (
          <ErrorMessage heading="We ran into a problem signing you in!" message={formError} />
        )}

        <Field
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          error={errors.email}
          onChange={(v) => {
            setEmail(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <PasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          value={password}
          error={errors.password}
          onChange={(v) => {
            setPassword(v);
            revalidate();
          }}
          onBlur={revalidate}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900" htmlFor="remember">
              Remember me
            </label>
          </div>
          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={busy}>
          {busy ? "Working…" : "Sign In"}
        </button>

        <p className="text-center text-sm text-gray-600">
          Or{" "}
          <Link
            to="/sign-up"
            search={true}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            register a new account
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
